// =============================================================================
// FILE: packages/renderer/src/services/TimezoneService.ts
//
// ARCHITECTURAL RULE
//   This is the SINGLE source of truth for all timezone calculations in
//   BIM OPS. Dashboard clocks, Calendar Engine reminders, Microsoft 365
//   sync, future Reminder Engine, and B6 Agent workflows MUST go through
//   this service. Any timezone-aware code outside this module is a bug.
//
//   Active project's IANA tz must be resolved via ProjectContext
//   (`useActiveProject().clientTimezone`) — NEVER via direct DB read or
//   localStorage in UI. ProjectContext is the only consumer that talks
//   to the project store; everything else takes the resolved string and
//   feeds it back into TimezoneService.
// =============================================================================

/** "America/Vancouver" → 'PST' / 'PDT'.  Uses Intl short-name format. */
function tzAbbreviation(tz: string, ref: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, timeZoneName: 'short',
    }).formatToParts(ref);
    const piece = parts.find((p) => p.type === 'timeZoneName');
    return piece?.value ?? tz;
  } catch {
    return tz;
  }
}

/** Returns the operating system / browser's IANA timezone. */
function getLocalTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/**
 * Returns a clock string in the given IANA timezone.
 * Defaults to 12-hour format (AM/PM). Set `hour12: false` for 24-hour.
 * Example outputs:  "02:30:45 PM"  |  "14:30:45"
 */
function formatTimeInZone(
  date: Date,
  tz: string,
  opts?: { withSeconds?: boolean; hour12?: boolean },
): string {
  const use12h = opts?.hour12 !== false; // default true
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour:   '2-digit',
    minute: '2-digit',
    second: opts?.withSeconds ? '2-digit' : undefined,
    hour12: use12h,
  }).format(date);
}

/** Returns the ISO weekday + short month-day-year, e.g. "Mon · 28 Feb 2026". */
function formatDateInZone(date: Date, tz: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz, weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  }).format(date);
}

/** "UTC+05:30" / "UTC-08:00".  Computed by formatting an epoch zero. */
function utcOffset(tz: string, ref: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, timeZoneName: 'longOffset',
    }).formatToParts(ref);
    const piece = parts.find((p) => p.type === 'timeZoneName');
    // longOffset returns "GMT+05:30" / "GMT-08:00" / "GMT"
    if (!piece) return 'UTC+00:00';
    const m = piece.value.match(/GMT([+-]\d{2}:\d{2})?/);
    if (!m) return 'UTC+00:00';
    return 'UTC' + (m[1] ?? '+00:00');
  } catch {
    return 'UTC+00:00';
  }
}

/** Returns the local-clock hour (0–23) for the given timezone. */
function hourInZone(date: Date, tz: string): number {
  const txt = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz, hour: '2-digit', hour12: false,
  }).format(date);
  return parseInt(txt, 10);
}

interface BusinessHoursOpts { startHour?: number; endHour?: number; workDays?: number[] }
const DEFAULT_BUSINESS_HOURS: Required<BusinessHoursOpts> = {
  startHour: 8, endHour: 18, workDays: [1, 2, 3, 4, 5], // Mon–Fri
};

/** True if the current local clock at `tz` is outside the business window.
 *  Default window: Mon–Fri 08:00–18:00. */
function isOutsideBusinessHours(
  tz: string, date: Date = new Date(), opts: BusinessHoursOpts = {},
): boolean {
  const { startHour, endHour, workDays } = { ...DEFAULT_BUSINESS_HOURS, ...opts };
  const dowName = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(date);
  const dowIdx  = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].indexOf(dowName);
  if (!workDays.includes(dowIdx)) return true;
  const h = hourInZone(date, tz);
  return h < startHour || h >= endHour;
}

/** Lazily compute the IANA timezone list. Falls back to a small hand-curated
 *  set on browsers that don't expose `Intl.supportedValuesOf`. */
let _allZones: string[] | null = null;
function allTimeZones(): string[] {
  if (_allZones) return _allZones;
  try {
    type IntlExt = typeof Intl & { supportedValuesOf?: (k: string) => string[] };
    const supported = (Intl as IntlExt).supportedValuesOf?.('timeZone');
    if (supported && supported.length) { _allZones = supported; return supported; }
  } catch { /* fall through */ }
  _allZones = [
    'UTC', 'America/Vancouver','America/Edmonton','America/Toronto','America/New_York',
    'America/Chicago','America/Denver','America/Los_Angeles','America/Mexico_City',
    'America/Sao_Paulo','Europe/London','Europe/Paris','Europe/Berlin','Europe/Madrid',
    'Europe/Rome','Europe/Amsterdam','Europe/Stockholm','Europe/Helsinki','Europe/Moscow',
    'Africa/Cairo','Africa/Johannesburg','Asia/Dubai','Asia/Karachi','Asia/Kolkata',
    'Asia/Bangkok','Asia/Singapore','Asia/Hong_Kong','Asia/Shanghai','Asia/Tokyo',
    'Asia/Seoul','Australia/Sydney','Australia/Melbourne','Pacific/Auckland',
  ];
  return _allZones;
}

/** Case-insensitive substring search over IANA names. */
function searchTimeZones(query: string, limit = 50): string[] {
  const q = query.trim().toLowerCase();
  const list = allTimeZones();
  if (!q) return list.slice(0, limit);
  return list.filter((tz) => tz.toLowerCase().includes(q)).slice(0, limit);
}

/** Sanity check — returns true if a string is a valid IANA tz name. */
function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export const TimezoneService = {
  getLocalTimezone,
  tzAbbreviation,
  formatTimeInZone,
  formatDateInZone,
  utcOffset,
  hourInZone,
  isOutsideBusinessHours,
  searchTimeZones,
  isValidTimeZone,
  allTimeZones,
} as const;

export type { BusinessHoursOpts };
